import { gql, useQuery } from '@apollo/client';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';

import { Loading } from 'src/components/common/loading/Loading';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { ProposalStatus } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { parseGraphqlMergedDefinitions } from 'src/lib/parseGraphqlMergedDefinitions';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { scrollToIfNotInView } from 'src/lib/scrollTo';

import { GraphRef } from '../../hooks/useGraphRef';
import {
  proposalChangesRouteConfig,
  proposalHomepageRouteConfig,
} from '../../routes';
import {
  FlatDiffItemFragment,
  groupDiffItems,
  parseCoordinate,
} from '../diffHelpers/diffHelpers';
import { getCoordinateType } from '../diffHelpers/getCoordinateType';
import { DiffSummaryDiffFragment } from '../DiffSummary';

import {
  DetailDiff,
  getIdFriendlyCoordinateName,
  splitOutTargetsFromCoordinate,
} from './DetailDiff';
import { ResetFiltersEmptyChangesState } from './ResetFiltersEmptyChangesState';

export const ChangesDiffs = ({
  proposalStatus,
  graphRef,
  oldSchemaHash,
  newSchemaHash,
  parentComments = [],
  collapsedState,
  setCollapsedState,
  canMakeComments,
  className,
  implementedChanges,
  includeImplementedChanges,
  canQueryLaunches,
  sourceGraphVariant,
}: {
  proposalStatus: GraphQLTypes.ProposalStatus | undefined;
  graphRef: GraphRef;
  oldSchemaHash?: string;
  newSchemaHash?: string;
  parentComments?: GraphQLTypes.ProposalCommentsParentCommentFragment[];
  collapsedState?: Record<string, boolean>;
  setCollapsedState?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  // whether or not comments are allowed in this view on the schema coordinate display
  canMakeComments: boolean;
  className?: string;
  implementedChanges: Omit<
    GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
    'subgraph'
  >[];
  includeImplementedChanges: boolean;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
}) => {
  const [isFullyMounted, setIsFullyMounted] = useState(false);
  const { linkedGroupName, linkedCoordinateName } = useRouteParams(
    proposalChangesRouteConfig,
    catchAllRouteConfig,
  );
  const isActivityFeed = !!useRouteMatch(
    proposalHomepageRouteConfig.definition,
  );

  const { data, loading } = useQuery<
    GraphQLTypes.ChangesDiffsQuery,
    GraphQLTypes.ChangesDiffsQueryVariables
  >(
    gql`
      query ChangesDiffsQuery(
        $graphId: ID!
        $newSdlHash: SHA256
        $oldSdlHash: SHA256
        $hasOldSdlHash: Boolean!
        $hasNewSdlHash: Boolean!
      ) {
        service(id: $graphId) {
          id
          oldDoc: doc(hash: $oldSdlHash) @include(if: $hasOldSdlHash) {
            source
          }
          newDoc: doc(hash: $newSdlHash) @include(if: $hasNewSdlHash) {
            source
          }
          flatDiff(newSdlHash: $newSdlHash, oldSdlHash: $oldSdlHash) {
            ... on FlatDiff {
              id
              ...DiffSummaryDiffFragment
              diff {
                ...FlatDiffItemFragment
              }
            }
          }
        }
      }
      ${DiffSummaryDiffFragment}
      ${FlatDiffItemFragment}
    `,
    {
      skip: !oldSchemaHash && !newSchemaHash,
      variables: {
        graphId: graphRef.graphId,
        newSdlHash: newSchemaHash,
        oldSdlHash: oldSchemaHash,
        hasNewSdlHash: !!newSchemaHash,
        hasOldSdlHash: !!oldSchemaHash,
      },
    },
  );

  const diffResult = data?.service?.flatDiff;
  const unimplementedDiffItems = useMemo(
    () =>
      diffResult?.__typename === 'FlatDiff'
        ? // NOTE: when implementedChanges is an empty array, it causes an infinite
          // render loop and leads to unexpected behavior.  We should default to
          // the diffResult if there are no implemented changes for the current schema.
          isMinLengthArray(1, implementedChanges)
          ? diffResult.diff.filter(
              (diffItem) =>
                !implementedChanges.some(
                  (change) =>
                    'coordinate' in change.diffItem &&
                    'coordinate' in diffItem &&
                    change.diffItem.coordinate === diffItem.coordinate &&
                    change.diffItem.__typename === diffItem.__typename,
                ),
            )
          : diffResult.diff
        : null,
    [diffResult, implementedChanges],
  );
  const diff = useMemo(
    () =>
      !includeImplementedChanges
        ? unimplementedDiffItems
        : diffResult?.__typename === 'FlatDiff'
          ? diffResult.diff
          : null,
    [diffResult, includeImplementedChanges, unimplementedDiffItems],
  );
  const groupedDiff = useMemo(() => diff && groupDiffItems(diff), [diff]);

  const schemas = useMemo(() => {
    const parseSdl = (sdl: string | undefined) => {
      return sdl ? parseGraphqlMergedDefinitions(sdl) : null;
    };
    return {
      oldSchema: parseSdl(data?.service?.oldDoc?.source),
      newSchema: parseSdl(data?.service?.newDoc?.source),
    };
  }, [data]);

  const changedTypeGroups = useMemo(() => {
    const coordinates = diff?.map(parseCoordinate) ?? [];
    const uniqueCoordinates = _.uniqBy(
      coordinates,
      (value) =>
        `${
          !('schemaExtension' in value) &&
          ('type' in value ? value.type : value.directive)
        }.${
          // NOTE: Some values may have the same name but be for a directive/type
          // we should not filter these items out
          'type' in value ? 'type' : 'directive' in value ? 'directive' : ''
        }`,
    );
    const sortedCoordinates = _.orderBy(uniqueCoordinates, [
      'type',
      'directive',
      'schemaDefinition',
    ]);
    const coordinateTypes =
      sortedCoordinates.flatMap((changedType) => {
        const oldType = getCoordinateType(changedType, schemas.oldSchema);
        const newType = getCoordinateType(changedType, schemas.newSchema);
        const typeName = oldType?.name ?? newType?.name;

        if (!typeName) {
          throw new Error('type is missing a name');
        } else if (
          oldType?.kind &&
          newType?.kind &&
          oldType.kind !== newType.kind
        ) {
          return [
            { typeName, kind: newType.kind, oldType: undefined, newType },
            { typeName, kind: oldType.kind, oldType, newType: undefined },
          ];
        } else {
          return {
            typeName,
            kind: newType?.kind ?? oldType?.kind,
            oldType,
            newType,
          };
        }
      }) ?? [];
    return Object.entries(_.groupBy(coordinateTypes, 'kind')) as [
      (typeof coordinateTypes)[number]['kind'],
      (typeof coordinateTypes)[number][],
    ][];
  }, [diff, schemas]);

  useEffect(() => {
    setCollapsedState?.((current) =>
      Object.fromEntries(
        changedTypeGroups.flatMap(([kind, group]) =>
          group.map(({ typeName }) => {
            const key = `${kind}${typeName}`;
            return [key, current[key] ?? false];
          }),
        ),
      ),
    );
  }, [changedTypeGroups, setCollapsedState]);

  const validGroupNames = useMemo(() => {
    return changedTypeGroups.map(([kind]) =>
      kind === 'SchemaDefinition'
        ? 'Schema'
        : kind === 'InputObjectTypeDefinition'
          ? 'Inputs'
          : (kind?.replace(/(Type)?Definition$/, 's') ?? ''),
    );
  }, [changedTypeGroups]);

  const { coordinate: linkedCoordinateNameWithoutTargets, target } = useMemo(
    () => splitOutTargetsFromCoordinate(linkedCoordinateName),
    [linkedCoordinateName],
  );

  // NOTE: We call this function within and outside the useEffect hook because it
  // forces the page to scroll to the proper element when it is rendered properly
  // removing either one of these causes the scroll to not work at all.
  useEffect(() => {
    scrollToIfNotInView(
      linkedGroupName && validGroupNames.includes(linkedGroupName)
        ? linkedCoordinateNameWithoutTargets
          ? `#${linkedGroupName} [id^=${getIdFriendlyCoordinateName(
              linkedCoordinateNameWithoutTargets,
              target,
            )}]`
          : `#${linkedGroupName}`
        : linkedCoordinateNameWithoutTargets
          ? `[id^=${getIdFriendlyCoordinateName(linkedCoordinateNameWithoutTargets, target)}]`
          : null,
    );
  }, [
    linkedGroupName,
    changedTypeGroups,
    linkedCoordinateNameWithoutTargets,
    validGroupNames,
    isFullyMounted,
    target,
  ]);

  if (loading) {
    return (
      <div className="mt-3">
        <Loading />
      </div>
    );
  }

  if (!isMinLengthArray(1, diff) && !isActivityFeed) {
    return (
      <ResetFiltersEmptyChangesState
        proposalIsImplemented={proposalStatus === ProposalStatus.IMPLEMENTED}
      />
    );
  }

  return (
    <div className={className}>
      {isMinLengthArray(1, changedTypeGroups) || !isActivityFeed ? (
        changedTypeGroups.map(([kind, nodes]) => {
          const groupTitle =
            kind === 'SchemaDefinition'
              ? 'Schema'
              : kind === 'InputObjectTypeDefinition'
                ? 'Inputs'
                : (kind?.replace(/(Type)?Definition$/, 's') ?? '');
          return (
            <div key={kind} id={groupTitle}>
              <div
                className={classnames(
                  'pb-3 font-semibold',
                  isActivityFeed ? 'text-base' : 'pt-6 text-lg',
                )}
              >
                <span
                  className={classnames(
                    linkedGroupName === groupTitle &&
                      !linkedCoordinateName &&
                      'bg-yellow-lightest',
                  )}
                >
                  {groupTitle}
                </span>
              </div>
              <div className="space-y-4">
                {nodes.map(({ typeName, oldType, newType }) => {
                  const diffGroups = groupedDiff?.[typeName] ?? [];
                  const diffGroup = !oldType
                    ? diffGroups[diffGroups.length - 1]
                    : diffGroups[0];
                  if (!diffGroup) return null;
                  const key = `${kind}${typeName}`;
                  const diffGroupIsPartiallyImplemented = diffGroup.some(
                    (diffItem) =>
                      'coordinate' in diffItem &&
                      unimplementedDiffItems?.some(
                        (item) =>
                          'coordinate' in item &&
                          item.coordinate === diffItem.coordinate,
                      ),
                  );

                  return (
                    <DetailDiff
                      graphRef={graphRef}
                      implementedChanges={implementedChanges}
                      shouldOnlyShowUnimplementedLines={
                        !includeImplementedChanges
                      }
                      canQueryLaunches={canQueryLaunches}
                      sourceGraphVariant={sourceGraphVariant}
                      key={key}
                      typeName={typeName}
                      oldType={oldType}
                      newType={newType}
                      diffGroup={diffGroup}
                      diffGroupIsPartiallyImplemented={
                        diffGroupIsPartiallyImplemented
                      }
                      collapsed={collapsedState?.[key] ?? false}
                      toggleCollapsed={() =>
                        setCollapsedState?.({
                          ...collapsedState,
                          [key]: !collapsedState?.[key],
                        })
                      }
                      parentComments={parentComments}
                      onMount={() => setIsFullyMounted(true)}
                      canMakeComments={canMakeComments}
                    />
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <i className="text-secondary">
          There are no semantic changes associated with this revision.
        </i>
      )}
    </div>
  );
};
